import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { PriorityDnDList } from '@/components/domain/PriorityDnDList';
import { CountryPicker } from '@/components/domain/CountryPicker';
import { useBookingPriorities, useUpdateBookingPriorities } from '@/api/queries/user';
import { useCountries } from '@/api/queries/reference';
import { IcPlus } from '@/components/ui/icons';
import { useUIStore } from '@/store/ui';
import type { BookingPriorityCountry } from '@/api/types';

export function SettingsPriorityEditPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useBookingPriorities();
  const update = useUpdateBookingPriorities();
  const countries = useCountries();
  const pushToast = useUIStore((s) => s.pushToast);

  const [items, setItems] = useState<BookingPriorityCountry[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (list.data) setItems(list.data);
  }, [list.data]);

  const save = async () => {
    try {
      await update.mutateAsync(items.map((it, i) => ({ country_code: it.country_code, position: i })));
      pushToast({ body: t('common.done'), variant: 'success' });
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('settings.priority_edit.title')} />
        <p style={{ color: 'var(--color-text-on-dark-muted)' }}>
          {t('settings.priority_edit.subtitle')}
        </p>
        {list.isLoading && <Spinner />}
        {items.length === 0 && !list.isLoading && (
          <div style={{ color: 'var(--color-text-on-dark-muted)' }}>{t('settings.priority_edit.empty')}</div>
        )}
        <PriorityDnDList
          items={items}
          onChange={setItems}
          onRemove={(code) => setItems((x) => x.filter((c) => c.country_code !== code))}
        />
        <Button block variant="secondary" startIcon={<IcPlus />} disabled={items.length >= 10} onClick={() => setOpen(true)}>
          {t('settings.priority_edit.add')}
        </Button>
        <Button block size="lg" variant="inverted" onClick={save} loading={update.isPending}>
          {t('common.save')}
        </Button>

        <BottomSheet open={open} onClose={() => setOpen(false)} title={t('settings.priority_edit.add')}>
          <CountryPicker
            exclude={items.map((i) => i.country_code)}
            onPick={(code, name) => {
              setItems((cur) => [
                ...cur,
                {
                  country_code: code,
                  country_name: name,
                  position: cur.length,
                },
              ]);
              setOpen(false);
            }}
          />
        </BottomSheet>
        {/* noop dependency just to keep TS happy */}
        {countries.data && <span hidden>{countries.data.length}</span>}
      </PageContainer>
    </AppShell>
  );
}
